
import React, { useState } from 'react';
import { Lock, Image as ImageIcon } from 'lucide-react';
import { BlurImage } from '@/components/shared/BlurImage';
import { useBlurPlaceholder } from '@/hooks/use-blur-placeholder';

interface PlaylistCardImageProps {
  image?: string | null;
  name: string;
  isPublic?: boolean;
  featured?: boolean;
  onClick?: () => void;
  className?: string;
}

export const PlaylistCardImage: React.FC<PlaylistCardImageProps> = ({
  image,
  name,
  isPublic = true,
  featured = false,
  onClick,
  className = "",
}) => {
  const [imageError, setImageError] = useState(false);
  
  if (!image) return null;
  
  // Clean the image URL to prevent issues
  const cleanImageUrl = (url: string): string => {
    try {
      // Fix incorrectly formatted storage URLs
      if (url.includes('supabase.co/storage') && url.includes('%252F')) {
        return url.replace(/%252F/g, '%2F');
      }
      
      // Remove API keys and tokens if present
      if (url.includes('key=') || url.includes('token=')) {
        const urlObj = new URL(url);
        urlObj.searchParams.delete('key');
        urlObj.searchParams.delete('token');
        return urlObj.toString();
      }
      
      return url;
    } catch (e) {
      console.warn('Error cleaning URL', e);
      return url;
    }
  };

  const cleanedImage = cleanImageUrl(image);
  const { blurDataURL } = useBlurPlaceholder(cleanedImage);
  
  const displayImage = imageError ? '/placeholder.svg' : cleanedImage;
  
  return (
    <div 
      className={`aspect-[16/9] relative overflow-hidden ${className}`}
      onClick={onClick}
    >
      {/* Error state */}
      {imageError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
          <ImageIcon className="w-8 h-8 text-gray-300" />
          <span className="text-sm text-gray-500 mt-2">Image unavailable</span>
        </div>
      )}
      
      {!imageError && (
        <BlurImage
          src={displayImage}
          alt={name}
          className="w-full h-full hover:scale-105 transition-transform duration-300"
          blurDataURL={blurDataURL}
          onError={() => {
            console.error('Image failed to load:', image);
            setImageError(true);
          }}
        />
      )}
      
      {featured && (
        <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium z-10">
          Featured
        </div>
      )}
      
      {!isPublic && (
        <div className="absolute top-2 left-2 bg-secondary text-secondary-foreground px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 z-10">
          <Lock className="w-3 h-3" />
          Private
        </div>
      )}
    </div>
  );
};
